import { FiMessageSquare } from 'react-icons/fi';

export const reportedSpeechData = {
  courseId: 'reported-speech',
  courseType: 'grammar',
  title: 'Reported Speech',
  icon: FiMessageSquare,
  overview: {
    impact: 'Report what others said — essential for academic contexts',
    focus: 'Tense backshift and reporting verbs',
    uses: 'Speaking, Writing Task 2 (citing opinions)',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Tense Backshift',
      content: `<p>When reporting, tenses shift one step back:</p>`,
      table: {
        headers: [
          'Direct Speech',
          'Reported Speech'
        ],
        rows: [
          [
            '\'I work here.\' (present simple)',
            'She said she worked there. (past simple)'
          ],
          [
            '\'I am working.\' (present continuous)',
            'She said she was working. (past continuous)'
          ],
          [
            '\'I have finished.\' (present perfect)',
            'She said she had finished. (past perfect)'
          ],
          [
            '\'I will help.\' (will)',
            'She said she would help. (would)'
          ]
        ]
      }
    },
    {
      title: 'Other Changes',
      content: `<p>Time and place references also change:</p>`,
      table: {
        headers: [
          'Direct',
          'Reported'
        ],
        rows: [
          [
            'today',
            'that day'
          ],
          [
            'tomorrow',
            'the next day / the following day'
          ],
          [
            'yesterday',
            'the day before / the previous day'
          ],
          [
            'here',
            'there'
          ],
          [
            'this',
            'that'
          ],
          [
            'now',
            'then'
          ]
        ]
      }
    },
    {
      title: 'Reporting Verbs',
      content: `<p>Vary your reporting verbs for higher scores:</p>`,
      points: [
        'say/tell (neutral): She said that...',
        'claim/argue (academic): The author claims that...',
        'suggest/recommend: He suggested going early.',
        'warn/advise: She warned us not to be late.',
        'deny/admit: He denied stealing the money.'
      ]
    }
  ],
  exercises: [
    {
      title: 'Convert to Reported Speech',
      type: 'select',
      instructions: 'Choose the correct reported form.',
      questions: [
        {
          text: '\'I am tired.\' → She said she ___ tired.',
          options: [
            'was',
            'is',
            'had been'
          ],
          answer: 'was',
          explanation: 'Present simple → past simple (am→was).'
        },
        {
          text: '\'I will come tomorrow.\' → He said he ___ come the next day.',
          options: [
            'would',
            'will',
            'had'
          ],
          answer: 'would',
          explanation: 'will → would; tomorrow → the next day.'
        },
        {
          text: '\'I have seen the film.\' → She said she ___ the film.',
          options: [
            'had seen',
            'has seen',
            'saw'
          ],
          answer: 'had seen',
          explanation: 'Present perfect → past perfect (have seen → had seen).'
        }
      ]
    },
    {
      title: 'Change to Reported Speech',
      type: 'fill-in',
      instructions: 'Fill in the blank to change each sentence into reported speech.',
      questions: [
        {
          text: '"I am happy." → She said she ___ happy.',
          answer: 'was',
          explanation: 'Present simple \'am\' shifts back to past simple \'was\' in reported speech.'
        },
        {
          text: '"We are leaving tomorrow." → They said they ___ leaving the next day.',
          answer: 'were',
          explanation: 'Present continuous \'are leaving\' shifts to past continuous \'were leaving\'; \'tomorrow\' becomes \'the next day\'.'
        },
        {
          text: '"I can speak French." → He said he ___ speak French.',
          answer: 'could',
          explanation: '\'Can\' shifts to \'could\' in reported speech.'
        },
        {
          text: '"I don\'t like this place." → She said she didn\'t like ___ place.',
          answer: 'that',
          explanation: '\'This\' changes to \'that\' in reported speech because the reference point shifts.'
        }
      ]
    },
    {
      title: 'Identify Tense Changes',
      type: 'select',
      instructions: 'Choose the correct tense change for reported speech.',
      questions: [
        {
          text: 'Direct: "I work here." → Reported: She said she ___ there.',
          options: [
            'worked',
            'works',
            'is working'
          ],
          answer: 'worked',
          explanation: 'Present simple → past simple: work → worked.'
        },
        {
          text: 'Direct: "I will help you." → Reported: He said he ___ help me.',
          options: [
            'would',
            'will',
            'could'
          ],
          answer: 'would',
          explanation: '\'Will\' shifts to \'would\' in reported speech.'
        },
        {
          text: 'Direct: "I can drive." → Reported: She said she ___ drive.',
          options: [
            'could',
            'can',
            'might'
          ],
          answer: 'could',
          explanation: '\'Can\' shifts to \'could\' in reported speech.'
        },
        {
          text: 'Direct: "I am studying." → Reported: He said he ___ studying.',
          options: [
            'was',
            'is',
            'had been'
          ],
          answer: 'was',
          explanation: 'Present continuous → past continuous: am studying → was studying.'
        }
      ]
    },
    {
      title: 'Report Questions',
      type: 'fill-in',
      instructions: 'Convert each direct question into reported speech.',
      questions: [
        {
          text: '"Where do you live?" → She asked where I ___.',
          answer: 'lived',
          explanation: 'Reported questions use statement word order (no inversion) and backshift: do live → lived.'
        },
        {
          text: '"Are you coming?" → He asked if I ___ coming.',
          answer: 'was',
          explanation: 'Yes/no questions use \'if\' or \'whether\'. \'Are\' shifts to \'was\'.'
        },
        {
          text: '"What time does the train leave?" → She asked what time the train ___.',
          answer: 'left',
          explanation: 'The wh-word stays, word order becomes normal (no \'does\'), and the verb backshifts: leave → left.'
        },
        {
          text: '"Have you finished?" → He asked if I ___ finished.',
          answer: 'had',
          explanation: 'Present perfect \'have finished\' backshifts to past perfect \'had finished\'.'
        }
      ]
    }
  ],
  tips: [
    'Remember: tenses shift back one step',
    'Time/place words change: today→that day, here→there',
    'Vary reporting verbs for higher Writing scores',
    'In academic writing, use: argue, claim, suggest, maintain'
  ],
  dailyPractice: [
    'Practice reported speech concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Reported Speech: Tense backshift rules, time/place changes, and reporting verbs (say, claim, argue, suggest). Important for IELTS academic writing and speaking.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...reportedSpeechData,
      title: reportedSpeechData.title + ' (Foundations)',
      overview: { ...reportedSpeechData.overview, duration: '15-20 minutes' },
      sections: reportedSpeechData.sections.slice(0, Math.max(2, Math.ceil(reportedSpeechData.sections.length / 2))),
      exercises: reportedSpeechData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...reportedSpeechData,
      title: reportedSpeechData.title + ' (Advanced)',
      overview: { ...reportedSpeechData.overview, duration: '30-35 minutes' },
      sections: reportedSpeechData.sections,
      exercises: reportedSpeechData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(reportedSpeechData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return reportedSpeechData;
}
